import { useMemo, useState } from 'react';

import {
  AngleDown,
  ChakraBox,
  ChakraFlex,
  ChakraSimpleGrid,
  DateFormat,
  DatePicker,
  EquipUIFireflyV2Theme,
  FAIconName,
  FireflyButton,
  Menu,
  MenuOption,
} from '@equip.health/ui';
import { DateTime } from 'luxon';
import { PatientEvents } from '~/analytics/patientEvents';
import ConfirmModal from '~/components/common/ConfirmModal';
import useUnsavedChangesGuard from '~/lib/hooks/useUnsavedChangesGuard';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import {
  DiagnosisStatus,
  DiagnosisStatusCode,
  isOpenDiagnosis,
} from '~/lib/util/clinicalProfile.util';
import { toMenuOption } from '~/lib/util/menu';
import {
  useAddPatientDiagnosisMutation,
  useEditPatientDiagnosisMutation,
} from '~/store/services/diagnoses';
import {
  useGetDiagnosisStatusesEnumQuery,
  useGetDiagnosisVoidReasonsEnumQuery,
} from '~/store/services/enumerations';

interface AddDiagnosisProps {
  diagnosis: PatientDiagnosis;
  primaryDiagnosisExists: boolean;
  onAdd: () => void;
  onDiscard: () => void;
  onStatusUpdate: (status: string) => void;
  patientId?: string;
  validatedDiagnosisHasCodeAlready: (
    diagnosisCode: string,
    updatedStatusCode: string,
    skipDiagnosisId?: string,
  ) => boolean;
}

const { primary } = EquipUIFireflyV2Theme.colors;
const ADD_PATIENT_DIAGNOSIS_IDENTIFIER = 'add-patient-diagnosis';

const AddOrEditDiagnosis = ({
  diagnosis,
  primaryDiagnosisExists,
  onAdd,
  onDiscard,
  onStatusUpdate,
  patientId,
  validatedDiagnosisHasCodeAlready,
}: AddDiagnosisProps) => {
  const mode = !diagnosis?.externalId ? 'ADD' : 'EDIT';

  const { data: diagnosisStatuses, isLoading: isDiagnosisStatusesLoading } =
    useGetDiagnosisStatusesEnumQuery();
  const {
    data: diagnosisVoidReasons,
    isLoading: isGetDiagnosisVoidReasonsLoading,
  } = useGetDiagnosisVoidReasonsEnumQuery();

  const {
    diagnosis: diagnosisDetails,
    endDate: patientDiagnosisEndDate,
    onsetDate: patientDiagnosisOnsetDate,
    primary: isSelectedAsPrimaryDiagnosis,
    statusCode: patientDiagnosisStatusCode,
    statusLabel: patientDiagnosisStatusLabel,
    statusReason: patientDiagnosisStatusReason,
  } = diagnosis || {};
  const { code: patientDiagnosisCode, codingSystem } = diagnosisDetails;

  const [onsetDate, setOnsetDate] = useState<Date>(
    patientDiagnosisOnsetDate
      ? DateTime.fromFormat(
          patientDiagnosisOnsetDate,
          DateFormat.shortYearMonthDay,
        ).toJSDate()
      : null,
  );
  const [isOnsetDateValid, setIsOnsetDateValid] = useState<boolean>(false);
  const [endDate, setEndDate] = useState<Date>(
    patientDiagnosisEndDate
      ? DateTime.fromFormat(
          patientDiagnosisEndDate,
          DateFormat.shortYearMonthDay,
        ).toJSDate()
      : null,
  );
  const [isEndDateValid, setIsEndDateValid] = useState<boolean>(false);
  const [statusLabel, setStatusLabel] = useState<string>(
    patientDiagnosisStatusLabel || DiagnosisStatus.PRELIMINARY,
  );
  const [statusCode, setStatusCode] = useState<DiagnosisStatusCode>(
    patientDiagnosisStatusCode || DiagnosisStatusCode.PRELIMINARY,
  );
  const [voidReason, setVoidReason] = useState<string>(
    patientDiagnosisStatusReason,
  );
  const [isPrimaryDiagnosisModalOpen, setIsPrimaryDiagnosisModalOpen] =
    useState<boolean>(false);
  const [isUnsavedAddDiagnosisChanges, setIsUnsavedAddDiagnosisChanges] =
    useState<boolean>(false);

  const [addPatientDiagnosis, { isLoading: isAddPatientDiagnosisLoading }] =
    useAddPatientDiagnosisMutation();
  const [editPatientDiagnosis, { isLoading: isEditPatientDiagnosisLoading }] =
    useEditPatientDiagnosisMutation();

  const { trackEvent } = useAnalytics();

  const currentStatusOptions = useMemo<MenuOption[]>(() => {
    return (
      diagnosisStatuses
        ?.filter((status) =>
          mode === 'ADD' ? status.code !== DiagnosisStatusCode.VOID : true,
        )
        .map(({ label }) => toMenuOption(label)) ?? []
    );
  }, [diagnosisStatuses]);

  const voidReasonsMenuOptions = useMemo<MenuOption[]>(
    () => diagnosisVoidReasons?.map(({ label }) => toMenuOption(label)) ?? [],
    [diagnosisVoidReasons],
  );

  const validateOnSetDate = (isValid: boolean): void => {
    setIsOnsetDateValid(isValid);
  };

  const validateEndDate = (isValid: boolean): void => {
    setIsEndDateValid(isValid);
  };
  const isVoidReasonRequired = statusCode === DiagnosisStatusCode.VOID;
  const isEndDateRequired = statusCode === DiagnosisStatusCode.RESOLVED;
  const saveDiagnosis = (): void => {
    const body: PersistPatientDiagnosisRequest = {
      code: patientDiagnosisCode,
      codingSystem,
      endDate: endDate
        ? DateTime.fromJSDate(endDate).toFormat(DateFormat.shortYearMonthDay)
        : '',
      onsetDate: onsetDate
        ? DateTime.fromJSDate(onsetDate).toFormat(DateFormat.shortYearMonthDay)
        : '',
      primary: isOpenDiagnosis(statusCode)
        ? isSelectedAsPrimaryDiagnosis
        : false,
      statusCode,
      statusReason: isVoidReasonRequired ? voidReason : null,
    };

    if (mode === 'EDIT') {
      editPatientDiagnosis({
        patientId,
        diagnosisId: diagnosis.externalId,
        body,
      }).then(() => {
        setIsUnsavedAddDiagnosisChanges(false);
        onAdd();
        onDiscard();
      });
    } else {
      addPatientDiagnosis({ patientId, body }).then(() => {
        setIsUnsavedAddDiagnosisChanges(false);
        onAdd();
        onDiscard();
      });
    }
  };

  const handleTrackEvent = () => {
    trackEvent(PatientEvents.medicalHistorySaveChangesButtonClicked, {
      Source: AnalyticsSourceValues.CLINICAL_PROFILE_TAB,
    });
  };
  const onSaveClick = (): void => {
    const alreadyExists = validatedDiagnosisHasCodeAlready(
      diagnosis?.diagnosis?.code,
      statusCode,
      diagnosis?.externalId,
    );
    if (alreadyExists) {
      setIsUnsavedAddDiagnosisChanges(false);
      onAdd();
      onDiscard();
      return;
    }
    handleTrackEvent();
    if (primaryDiagnosisExists && isSelectedAsPrimaryDiagnosis) {
      setIsPrimaryDiagnosisModalOpen(true);
    } else {
      saveDiagnosis();
    }
  };
  const isOnsetDateRequired =
    statusCode === DiagnosisStatusCode.ACTIVE ||
    statusCode === DiagnosisStatusCode.RESOLVED;

  const isSubmitButtonIsDisabled =
    (onsetDate && !isOnsetDateValid) ||
    (endDate && !isEndDateValid) ||
    (isOnsetDateRequired && !onsetDate) ||
    (isEndDateRequired && !endDate) ||
    (isVoidReasonRequired && !voidReason);

  const { UnsavedChangesModal, guardedDismiss } = useUnsavedChangesGuard({
    isDirty: isUnsavedAddDiagnosisChanges,
    onDiscard,
  });

  const onFieldChange =
    <T,>(callback: (callbackValue: T) => void) =>
    (value: T) => {
      setIsUnsavedAddDiagnosisChanges(true);
      callback(value);
    };

  return (
    <>
      {UnsavedChangesModal}
      <ConfirmModal
        body="This patient already has a primary diagnosis. By adding a new one, the existing primary diagnosis will become secondary. Are you sure you wish to proceed?"
        cancelLabel="Cancel"
        confirmLabel="Set to primary"
        confirmVariant="accent.critical"
        id="add-diagnosis"
        isOpen={isPrimaryDiagnosisModalOpen}
        onClose={() => setIsPrimaryDiagnosisModalOpen(false)}
        onConfirm={() => {
          setIsPrimaryDiagnosisModalOpen(false);
          saveDiagnosis();
        }}
        title="Change Primary Diagnosis"
      />
      <ChakraSimpleGrid
        columns={3}
        marginBottom="25px"
        marginTop="20px"
        spacing={3}
      >
        <ChakraBox display="flex" flexDirection="column" width="100%">
          <DatePicker
            color="neutral.secondary"
            id={`${ADD_PATIENT_DIAGNOSIS_IDENTIFIER}__onset__date-picker`}
            isRequired={isOnsetDateRequired}
            label="Onset"
            labelForErrorMessage="Onset date"
            maxDate={endDate ?? null}
            onSelect={onFieldChange<Date>((date: Date) => setOnsetDate(date))}
            onValidateDate={validateOnSetDate}
            placeHolderText="MM/DD/YYYY"
            value={onsetDate}
          />
        </ChakraBox>
        <ChakraBox display="flex" flexDirection="column" width="100%">
          <Menu
            backgroundColor="neutral.background.secondary"
            buttonContent={statusLabel}
            buttonHeight="48px"
            buttonVariant="ghost"
            icon={<AngleDown fill={primary[100]} height="15px" width="15px" />}
            id={`${ADD_PATIENT_DIAGNOSIS_IDENTIFIER}__current-state`}
            isLoading={isDiagnosisStatusesLoading}
            label="Current state"
            listMaxHeight="300px"
            menuWidth="100%"
            onSelect={onFieldChange<string>((value: string) => {
              setStatusLabel(value);
              setStatusCode(
                DiagnosisStatusCode[
                  diagnosisStatuses.find((status) => status.label === value)
                    .code
                ],
              );
              onStatusUpdate(
                diagnosisStatuses.find((status) => status.label === value).code,
              );
            })}
            options={currentStatusOptions}
            placeholder="Select current state ..."
            textAlign="left"
          />
        </ChakraBox>
        <ChakraBox display="flex" flexDirection="column" width="100%">
          <DatePicker
            color="neutral.secondary"
            id={`${ADD_PATIENT_DIAGNOSIS_IDENTIFIER}__end-date__date-picker`}
            isDisabled={isOpenDiagnosis(statusCode)}
            isRequired={isEndDateRequired}
            label="End date"
            labelForErrorMessage="End date"
            minDate={onsetDate ?? null}
            onSelect={onFieldChange<Date>((date: Date) => setEndDate(date))}
            onValidateDate={validateEndDate}
            placeHolderText="MM/DD/YYYY"
            value={endDate}
          />
        </ChakraBox>
      </ChakraSimpleGrid>
      <ChakraBox marginBottom="25px">
        {isVoidReasonRequired && (
          <Menu
            backgroundColor="neutral.background.secondary"
            buttonContent={voidReason}
            buttonHeight="48px"
            buttonVariant="ghost"
            icon={<AngleDown fill={primary[100]} height="15px" width="15px" />}
            id={`${ADD_PATIENT_DIAGNOSIS_IDENTIFIER}__void-reason`}
            isFullWidth
            isLoading={isGetDiagnosisVoidReasonsLoading}
            isRequired={isVoidReasonRequired}
            label="Void reason"
            listMaxHeight="300px"
            onSelect={onFieldChange<string>((value: string) =>
              setVoidReason(value),
            )}
            options={voidReasonsMenuOptions}
            overflow="scroll"
            placeholder="Select one option..."
            textAlign="left"
          />
        )}
      </ChakraBox>
      <ChakraFlex alignItems="center" justifyContent="flex-end">
        <ChakraFlex gap="8px">
          <FireflyButton
            id={`${ADD_PATIENT_DIAGNOSIS_IDENTIFIER}__discard__button`}
            onClick={guardedDismiss}
            variant="secondary"
            size="small"
          >
            Discard
          </FireflyButton>
          <FireflyButton
            id={`${ADD_PATIENT_DIAGNOSIS_IDENTIFIER}__save-changes__button`}
            isDisabled={isSubmitButtonIsDisabled}
            isLoading={
              isAddPatientDiagnosisLoading || isEditPatientDiagnosisLoading
            }
            size="small"
            rightIcon={FAIconName.checkRegular}
            onClick={onSaveClick}
          >
            Save changes
          </FireflyButton>
        </ChakraFlex>
      </ChakraFlex>
    </>
  );
};

export default AddOrEditDiagnosis;
